import {
  type CreateExamEventsInput,
  ExamType,
  type ValidateExamEventsCreationInput,
} from '@exam-center/graphql';
import { dayjs } from '@shared/utils';

import {
  EventEndType,
  type ExamDay,
  type RecurringExamEventCreationFormType,
  type ValidatedExamEventWithClientId,
} from '../../providers';
import { getExamTime } from '../exam-event-creation-logic';

const WEEKDAY_TO_INDEX: Record<ExamDay, number> = {
  SUN: 0,
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
};

function convertToStartOfMinute(date: Date): Date {
  return dayjs(date).startOf('minute').toDate();
}

function convertToISODate(date: Date): Date {
  return new Date(date.toISOString());
}

function getExamDatesByExamDays(start: Date, end: Date, selectedExamDays: ExamDay[]): Date[] {
  const s = dayjs(start).startOf('day');
  const e = dayjs(end).endOf('day');

  if (!s.isValid() || !e.isValid() || s.isAfter(e)) return [];

  const wantedDays = new Set(selectedExamDays.map((d) => WEEKDAY_TO_INDEX[d]));
  const result: Date[] = [];

  for (let d = s; d.isBefore(e) || d.isSame(e, 'day'); d = d.add(1, 'day')) {
    if (wantedDays.has(d.day())) {
      result.push(d.toDate());
    }
  }

  return result;
}

function getRegistrationEndedAt(
  eventStartDate: { type: EventEndType; date: number | undefined | null },
  examStartDate: Date,
): Date {
  if (eventStartDate.type === EventEndType.ON_EXAM_DATE) {
    return dayjs(examStartDate).subtract(30, 'minute').toDate();
  } else if (eventStartDate.type === EventEndType.BEFORE_EXAM_DATE && eventStartDate.date) {
    return dayjs(examStartDate).subtract(eventStartDate.date, 'day').endOf('day').toDate();
  }
  return dayjs(examStartDate).toDate();
}

export function mapRecurringExamEventCreationFormToValidateExamEventsCreationInput(
  data: RecurringExamEventCreationFormType,
  examCenterCode: string,
  examCoursesOptions: { label: string; value: string }[],
): ValidateExamEventsCreationInput {
  if (
    !data.examEventName ||
    !data.registrationType ||
    !data.examineeImportMethod ||
    !data.eventStartDate ||
    !data.eventEndDate ||
    !data.examDateGroup?.date ||
    !data.examDateGroup?.startTime ||
    !data.examDateGroup?.endTime ||
    !data.location?.province ||
    !data.location?.locationName ||
    !data.location?.room ||
    !data.location?.seat ||
    !data.examDateGroup?.examRecurringStartDate ||
    !data.examDateGroup?.examRecurringEndDate ||
    !data.eventEndDate.type ||
    (data.eventEndDate.type === EventEndType.BEFORE_EXAM_DATE && !data.eventEndDate.date)
  ) {
    throw new Error('Invalid exam event creation form data');
  }

  const examEventDates = [];

  const examDates = getExamDatesByExamDays(
    data.examDateGroup.examRecurringStartDate,
    data.examDateGroup.examRecurringEndDate,
    data.examDateGroup.date,
  );

  for (const examDate of examDates) {
    const { examStartTime, examEndTime } = getExamTime(
      examDate,
      data.examDateGroup.startTime,
      data.examDateGroup.endTime,
    );

    const examStartTimeISO = convertToISODate(examStartTime);
    const examEndTimeISO = convertToISODate(examEndTime);

    const sessions = [
      {
        round: 1,
        startedAt: examStartTimeISO,
        endedAt: examEndTimeISO,
      },
    ];

    const registrationEndedAt = getRegistrationEndedAt(
      { type: data.eventEndDate.type, date: data.eventEndDate.date ?? null },
      examStartTime,
    );

    examEventDates.push({
      sessions,
      registrationStartedAt: convertToISODate(convertToStartOfMinute(data.eventStartDate)),
      registrationEndedAt: convertToISODate(convertToStartOfMinute(registrationEndedAt)),
      examStartedAt: examStartTimeISO,
      examEndedAt: examEndTimeISO,
    });
  }
  const examCourseValues = data.examCourses?.map((course) => course.value).filter(Boolean) ?? [];

  const isAllSelected = examCourseValues.includes('all');

  const examCourseIds = isAllSelected
    ? examCoursesOptions.map((course) => course.value)
    : examCourseValues;

  return {
    seat: data.location.seat,
    roomIds: Object.entries(data.location.room)
      .filter(([, isSelected]) => isSelected)
      .map(([roomId]) => roomId),
    registrationType: data.registrationType,
    examineeImportMethod: data.examineeImportMethod,
    proctorIds: data.mainProctors?.map((proctor) => proctor.value) || [],
    name: data.examEventName,
    metadata: {
      applicantGroup: {
        type: data.groupType?.value || null,
        code: data.groupCode || null,
      },
    },
    examType: ExamType.Computer,
    examLocationId: data.location.locationName.value,
    examEventDates: examEventDates,
    examCourseIds,
    examCenterCode,
  };
}

export function mapRecurringExamEventCreationFormToCreateExamEventsInput(
  data: RecurringExamEventCreationFormType,
  validateExamEventsData: ValidatedExamEventWithClientId[],
  examCenterCode: string,
  examCoursesOptions: { label: string; value: string }[],
): CreateExamEventsInput {
  if (
    !data.examEventName ||
    !data.registrationType ||
    !data.examineeImportMethod ||
    !data.eventStartDate ||
    !data.eventEndDate ||
    !data.examDateGroup?.date ||
    !data.examDateGroup?.startTime ||
    !data.examDateGroup?.endTime ||
    !data.location?.province ||
    !data.location?.locationName ||
    !data.location?.room ||
    !data.location?.seat ||
    !data.examDateGroup?.examRecurringStartDate ||
    !data.examDateGroup?.examRecurringEndDate ||
    !data.eventEndDate.type ||
    (data.eventEndDate.type === EventEndType.BEFORE_EXAM_DATE && !data.eventEndDate.date)
  ) {
    throw new Error('Invalid exam event creation form data');
  }

  const examEventDates = [];

  const examDates = getExamDatesByExamDays(
    data.examDateGroup.examRecurringStartDate,
    data.examDateGroup.examRecurringEndDate,
    data.examDateGroup.date,
  );

  for (const examDate of examDates) {
    const { examStartTime, examEndTime } = getExamTime(
      examDate,
      data.examDateGroup.startTime,
      data.examDateGroup.endTime,
    );

    const examStartTimeISO = convertToISODate(examStartTime);
    const examEndTimeISO = convertToISODate(examEndTime);

    const sessions = [
      {
        round: 1,
        startedAt: examStartTimeISO,
        endedAt: examEndTimeISO,
      },
    ];

    const registrationEndedAt = getRegistrationEndedAt(
      { type: data.eventEndDate.type, date: data.eventEndDate.date ?? null },
      examStartTime,
    );
    const checkExamInValidatedData = validateExamEventsData.find((exam) =>
      dayjs(exam.examStartedAt).isSame(examStartTimeISO),
    );
    if (checkExamInValidatedData?.isValid) {
      examEventDates.push({
        sessions,
        registrationStartedAt: convertToISODate(convertToStartOfMinute(data.eventStartDate)),
        registrationEndedAt: convertToISODate(convertToStartOfMinute(registrationEndedAt)),
        examStartedAt: examStartTimeISO,
        examEndedAt: examEndTimeISO,
        requestId: checkExamInValidatedData.requestId,
      });
    }
  }
  const examCourseValues = data.examCourses?.map((course) => course.value).filter(Boolean) ?? [];

  const isAllSelected = examCourseValues.includes('all');

  const examCourseIds = isAllSelected
    ? examCoursesOptions.map((course) => course.value)
    : examCourseValues;

  return {
    seat: data.location.seat,
    roomIds: Object.entries(data.location.room)
      .filter(([, isSelected]) => isSelected)
      .map(([roomId]) => roomId),
    registrationType: data.registrationType,
    examineeImportMethod: data.examineeImportMethod,
    proctorIds: data.mainProctors?.map((proctor) => proctor.value) || [],
    name: data.examEventName,
    metadata: {
      applicantGroup: {
        type: data.groupType?.value || null,
        code: data.groupCode || null,
      },
    },
    examType: ExamType.Computer,
    examLocationId: data.location.locationName.value,
    examEventDates: examEventDates,
    examCourseIds,
    examCenterCode,
  };
}
